import { useMutation } from '@tanstack/react-query';
import { Col, Row, Space } from 'antd';
import CNPlanApis from 'apis/CNPlanApis';
import { IGetTrackRevenueTeamSale } from 'apis/GraphQL/Variables/CNPlan';
import { IGetAllHoRevenuePlanBranchByPlantAndYear } from 'apis/GraphQL/Variables/hoFollowCustomer';
import { PlantApi } from 'apis/PlantApis';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import useQueryParam from 'hooks/useQueryPrams';
import { IPlant } from 'interfaces/Plant/Plant';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import Content from 'layouts/Content';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import path from 'routes/Path';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { compareData, generateUUID } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IFilterRevenuePlanBranch } from '../HOFollowPlan/HOFollowPlan';
import FilterCNFollowPlan from './FilterCNFollowPlan';
import { StatusProposedChange } from './StatusProposedChange';
import { TableCNFollowPlan } from './TableCNFollowPlan';

const { EyeOutlined } = icons;

export interface IData {
  items: any[];
  pageInfo?: any;
}

interface IPlanData {
  plan: IData;
  progress: any[];
}

const initPlanData: IPlanData = {
  plan: { items: [] },
  progress: [],
};

const CNFollowPlan = () => {
  const navigate = useNavigate();

  const planTableRef = useRef<any>(null);

  const user = useUser(),
    { setTitle, setBreadcrumb } = useTitle();

  const [name, setName] = useState<any>([]),
    [isOpen, setIsOpen] = useState(false),
    [plants, setPlants] = useState<IPlant[]>([]),
    [differences, setDifferences] = useState<any>([]),
    [recommendBr] = useState<any>([]),
    [filterData, setFilterData] = useState<IFilterRevenuePlanBranch>({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });
  const [data, setData] = useState<any>({});
  const [planData, setPlanData] = useState<IPlanData>(initPlanData);

  const defaultFilter = useMemo(() => {
    if (user.user?.isCN)
      return {
        plantId: (user.user as any)?.plantCode,
      };

    if (user.user?.isSale)
      return {
        plantId: (user.user as any)?.plantCode,
        userId: (user.user as any)?.id,
      };

    return {};
  }, [user.user]);

  const year = new Date().getFullYear();

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    page_size = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const {
    isPending: isGetRevenuePlanTeamSale,
    mutate: getRevenuePlanTeamSale,
  } = useMutation({
    mutationFn: (params: IGetTrackRevenueTeamSale) =>
      CNPlanApis.getTrackRevenueTeamSale(params),
    onSuccess: (res, variables) => {
      const data = res.data.getTrackRevenueTeamSale || {};
      const items = (data.items || []).map((item: any) => ({
        ...item,
        id: generateUUID(),
      }));

      if (variables.export) {
        exportExcel_v2(
          variables.export.headerRow,
          data?.items || [],
          'Vận hành - [CN] Theo dõi KH',
        );
      } else
        setData({
          ...data,
          items,
        });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getPlantNameById } = useMutation({
    mutationFn: (params?: any) => PlantApi.getPlantById(params),
    onSuccess: (response: any) => {
      const { data } = response;
      setName(data.getPlant.items.map((i: any) => i.name));
      setPlants(data.getPlant.items);
    },
  });

  useEffect(() => {
    const differences = compareData(recommendBr, planData.plan.items);
    setDifferences(differences);
  }, [recommendBr, planData.plan]);

  useEffect(() => {
    const params: IGetAllHoRevenuePlanBranchByPlantAndYear = {
      plantId: user.user?.plantCode,
      year: year.toString(),
    };

    if (user.user?.isAdmin || user.user?.isHO) {
      getPlantNameById(undefined);
    } else getPlantNameById(params.plantId);
  }, []);

  const getParamsTrackRevenueTeamSale = useCallback(
    ({
      headerRow,
      isRefresh,
      totalItems,
    }: {
      headerRow?: ColumnsTypeCustom;
      isRefresh?: boolean;
      totalItems?: number;
    }): IGetTrackRevenueTeamSale | undefined => {
      const { year, month, plantId, typeRevenue, teamSalesId } = filterData;

      if (!year || !month) return undefined;

      const take = headerRow ? (totalItems ?? 1000) : page_size;
      const skip = headerRow || isRefresh ? 0 : (page - 1) * page_size;

      return {
        take,
        skip,
        year: dayjs(year).year(),
        fromMonth: dayjs(month[0]).month() + 1,
        toMonth: dayjs(month[1]).month() + 1,
        plantId,
        typeRevenue,
        teamSalesId: teamSalesId ? teamSalesId.split('.')[0] : undefined,
        ...(headerRow ? { export: { headerRow } } : {}),
      };
    },
    [filterData, page, page_size],
  );

  useEffect(() => {
    const params = getParamsTrackRevenueTeamSale({});
    if (!params) return;

    getRevenuePlanTeamSale(params);
  }, [getParamsTrackRevenueTeamSale, getRevenuePlanTeamSale]);

  useEffect(() => {
    setTitle(`[CN] Theo dõi KH`);
    setBreadcrumb([{ title: 'Vận hành' }, { title: '[CN] Theo dõi KH' }]);
  }, [setBreadcrumb, setTitle]);

  const handleExportData = useCallback(() => {
    if (!planTableRef.current) return;

    const params = getParamsTrackRevenueTeamSale({
      headerRow: planTableRef.current.headerRow,
      isRefresh: true,
      totalItems: data.pageInfo?.totalItems,
    });
    if (!params) return;

    getRevenuePlanTeamSale(params);
  }, [
    data.pageInfo?.totalItems,
    getParamsTrackRevenueTeamSale,
    getRevenuePlanTeamSale,
  ]);

  const handleOnCancelFilter = useCallback(() => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });
  }, [navigate, page_size]);

  const handleOnOkFilter = (values: any) => {
    navigate(`?page=1&page_size=${page_size}`);
    setFilterData(values);
  };

  return (
    <>
      <Row gutter={[8, 16]} justify={'end'} align={'middle'}>
        <Col>
          <ButtonFilter isOpen={isOpen} setIsOpen={setIsOpen} />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportData} />
        </Col>
        <Col>
          <Button
            onClick={() =>
              navigate(`${path.CN_FOLLOW_PLAN_DETAIL}`, {
                state: {
                  year:
                    dayjs(filterData.year).year() ?? new Date().getFullYear(),
                  plant_id: filterData.plantId,
                  team_sales_id: filterData.teamSalesId,
                },
              })
            }
            title="Xem chi nhánh"
            iconBefore={<EyeOutlined className="text-xl" />}
          />
        </Col>
      </Row>
      <FilterCNFollowPlan
        isOpen={isOpen}
        plantData={plants}
        defaultFilter={defaultFilter}
        onFilter={handleOnOkFilter}
        onCancel={handleOnCancelFilter}
      />
      {recommendBr.length ? (
        <Content>
          <TableCNFollowPlan
            data={recommendBr}
            title={() => (
              <Row justify={'space-between'} align={'middle'}>
                <Col className="text-[16px] leading-[22.4px] font-bold">
                  <Space>
                    <p>Chi nhánh đề xuất</p>
                    <StatusProposedChange
                      title="Chờ phê duyệt"
                      type="pending"
                    />
                  </Space>
                </Col>
                <Col>Đơn vị: triệu VND</Col>
              </Row>
            )}
            differences={differences}
            pagination={false}
          />
        </Content>
      ) : (
        <></>
      )}
      <Content>
        <TableCNFollowPlan
          ref={planTableRef}
          differences={[]}
          data={data}
          loading={isGetRevenuePlanTeamSale}
          title={() => (
            <Row justify={'end'} align={'middle'}>
              <Col>Đơn vị: triệu VND</Col>
            </Row>
          )}
        />
      </Content>
    </>
  );
};

export default CNFollowPlan;
